import type {Meta, StoryObj} from "@storybook/web-components";
import type {NotificationSettings} from "../../src/components/notification-settings/notification-settings";
import {html} from "lit";

//! these should be html-elements instead of strings
//! string controls output string (duh) that can be used like ${unsafeHTML(greeter_headline)}, see https://lit.dev/docs/templates/directives/#unsafehtml

type SlotArgs = {
	greeter_headline: string;
	greeter_text: string;

	categories_headline: string;
	categories_text: string;

	categories_table_heading: string;
	categories_table_notification_channel_heading: string;
	categories_table_email_channel_heading: string;

	categories_save_button_label: string;
	categories_cancel_button_label: string;

	assets_headline: string;
	assets_text: string;

	assets_table_heading: string;

	assets_save_button_label: string;
	assets_cancel_button_label: string;
};

const meta: Meta<NotificationSettings & SlotArgs> = {
	title: "Notification/Notification Settings",
	component: "uxn-notification-settings",

	decorators: [
		(story) =>
			// spell-checker: disable
			html` <fragment>
				<ux-u-silent-auth
					issuer="https://identity-s.trumpf.com"
					client-id="zyffE9ozijzUpQqdaOyteGMJfhMa"
					client-name="notificationmanagement"
					scope="openid"
					debug="true"
				></ux-u-silent-auth>
				${story()}
			</fragment>`,
		// spell-checker: enable
	],

	render: ({
		notificationsTooltip,
		emailTooltip,
		locale,
		stage,
		debug,

		greeter_headline,
		greeter_text,

		categories_headline,
		categories_text,

		categories_table_heading,
		categories_table_notification_channel_heading,
		categories_table_email_channel_heading,

		categories_save_button_label,
		categories_cancel_button_label,

		assets_headline,
		assets_text,

		assets_table_heading,

		assets_save_button_label,
		assets_cancel_button_label,
	}) => html`
		<uxn-notification-settings
			notifications-tooltip="${notificationsTooltip}"
			email-tooltip="${emailTooltip}"
			locale="${locale}"
			stage="${stage}"
			debug="${debug}"
		>
			<div class="headline-big" slot="greeter_headline">${greeter_headline}</div>
			<p slot="greeter_text">${greeter_text}</p>

			<div class="headline-big" slot="categories_headline">${categories_headline}</div>
			<p slot="categories_text">${categories_text}</p>

			<span slot="categories_table_heading">${categories_table_heading}</span>
			<span slot="categories_table_notification_channel_heading">${categories_table_notification_channel_heading}</span>
			<span slot="categories_table_email_channel_heading">${categories_table_email_channel_heading}</span>

			<span slot="categories_save_button_label">${categories_save_button_label}</span>
			<span slot="categories_cancel_button_label">${categories_cancel_button_label}</span>

			<div class="headline-big" slot="assets_headline">${assets_headline}</div>
			<p slot="assets_text">${assets_text}</p>

			<span slot="assets_table_heading">${assets_table_heading}</span>

			<span slot="assets_save_button_label">${assets_save_button_label}</span>
			<span slot="assets_cancel_button_label">${assets_cancel_button_label}</span>
		</uxn-notification-settings>
	`,

	argTypes: {
		notificationsTooltip: {control: "text"},
		emailTooltip: {control: "text"},
		locale: {control: "text"},
		stage: {control: "select", options: ["PROD", "QA", "INT"]},
		debug: {control: "boolean"},

		//! these should be {summary: "html-elements"} instead of strings
		greeter_headline: {
			name: '<slot name = "greeter_headline" />',
			description: "a custom topic headline",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
		greeter_text: {
			name: '<slot name = "greeter_text" />',
			description: "a custom topic text",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},

		categories_headline: {
			name: '<slot name = "categories_headline" />',
			description: "channels/categories headline",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
		categories_text: {
			name: '<slot name = "categories_text" />',
			description: "channels/categories text",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},

		categories_table_heading: {
			name: '<slot name = "categories_table_heading" />',
			description: "table heading",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
		categories_table_notification_channel_heading: {
			name: '<slot name = "categories_table_notification_channel_heading" />',
			description: "channel column header",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
		categories_table_email_channel_heading: {
			name: '<slot name = "categories_table_email_channel_heading" />',
			description: "email column header",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},

		categories_save_button_label: {
			name: '<slot name = "categories_save_button_label" />',
			description: "save-button label",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
		categories_cancel_button_label: {
			name: '<slot name = "categories_cancel_button_label" />',
			description: "cancel-button label",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},

		assets_headline: {
			name: '<slot name = "assets_headline" />',
			description: "assets headline",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
		assets_text: {
			name: '<slot name = "assets_text" />',
			description: "assets text",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},

		assets_table_heading: {
			name: '<slot name = "assets_table_heading" />',
			description: "assets table heading",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},

		assets_save_button_label: {
			name: '<slot name = "assets_save_button_label" />',
			description: "save-button label",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
		assets_cancel_button_label: {
			name: '<slot name = "assets_cancel_button_label" />',
			description: "cancel-button label",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
	},
};

export default meta;
type Story = StoryObj<NotificationSettings & SlotArgs>;

export const Default: Story = {
	args: {
		notificationsTooltip: "With the checkbox activated, you will receive notifications in MyTRUMPF.",
		locale: "en_US",
		emailTooltip:
			"With the checkbox activated, you will receive notifications via email. Please note that e-mail notifications here include your individual and transactional notifications. You can unsubscribe from TRUMPF newsletters here.",
		stage: "QA",
		debug: true,

		greeter_headline: "Notification Settings",
		greeter_text: "In this tab you can configure your notification settings.",

		categories_headline: "Channels/categories",
		categories_text: "Select below how you want to receive notifications.",

		categories_table_heading: "Category",
		categories_table_notification_channel_heading: "Notification",
		categories_table_email_channel_heading: "Email",

		categories_save_button_label: "Save changes",
		categories_cancel_button_label: "Discard changes",

		assets_headline: "Choose assets",
		assets_text: "Select the systems you would like to receive notifications about below.",

		assets_table_heading: "My Trumpf machines",

		assets_save_button_label: "Save changes",
		assets_cancel_button_label: "Discard changes",
	},
};
